/** @jsxImportSource @emotion/react */
import React from 'react'
import { css, keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'

export interface NavigationTab {
  id: string
  title: string
  subtitle: string
  status: 'ready' | 'pending' | 'incomplete'
  disabled?: boolean
}

interface SchedulerNavigationBarProps {
  tabs: NavigationTab[]
  activeTabId: string
  onTabChange: (tabId: string) => void
  loading?: boolean
  onDownload?: () => void
  onSettings?: () => void
  onMoreOptions?: () => void
}

const SchedulerNavigationBar: React.FC<SchedulerNavigationBarProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  loading,
  onDownload,
  onSettings,
  onMoreOptions,
}) => {
  // Container: matches header structure exactly
  // padding: 0 88px to match header's horizontal padding (header has 40px 88px)
  const containerStyles = css`
    background-color: ${colors.SECONDARY_400};
    border-bottom: 1px solid ${colors.SECONDARY_200};
    padding: 0 88px;
  `

  // Inner container: matches headerInnerStyles exactly
  // max-width: 1440px, margin: 0 auto, flex, justify-content: space-between
  const innerContainerStyles = css`
    max-width: 1440px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `

  // Tabs container - holds all tabs in a row
  const tabsContainerStyles = css`
    display: flex;
    align-items: center;
  `

  // Tab button styles
  // From Figma: flex gap-[8px] items-center px-[16px] py-[8px]
  // Active: border-b-2 border-[#f2b400]
  const getTabStyles = (isActive: boolean, isDisabled?: boolean) => css`
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 16px;
    cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
    border: none;
    background: transparent;
    border-bottom: 2px solid ${isActive ? colors.PRIMARY_300 : 'transparent'};
    flex-shrink: 0;
    transition: border-color 0.2s ease;
    opacity: ${isDisabled ? 0.5 : 1};

    &:hover {
      border-bottom-color: ${isActive || isDisabled
        ? isActive
          ? colors.PRIMARY_300
          : 'transparent'
        : colors.SECONDARY_200};
    }
  `

  // Tab content container (title + subtitle)
  const tabContentStyles = css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  `

  // Tab title - 14px, bold for active, regular for inactive
  const getTabTitleStyles = (isActive: boolean) => css`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: ${isActive ? 700 : 400};
    line-height: 20.3px;
    color: ${colors.WHITE};
    margin: 0;
  `

  // Tab subtitle - 12px, regular, gray
  const tabSubtitleStyles = css`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 17.4px;
    color: ${colors.NEUTRAL_100};
    margin: 0;
  `

  // Shimmer placeholder bar for loading state subtitles
  const shimmer = keyframes`
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  `

  const tabSubtitleShimmerStyles = css`
    height: 12px;
    width: 80px;
    border-radius: 4px;
    margin: 3px 0;
    background: linear-gradient(
      90deg,
      ${colors.SECONDARY_200} 25%,
      ${colors.SECONDARY_100} 50%,
      ${colors.SECONDARY_200} 75%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s ease-in-out infinite;
  `

  // Status icon container - 24x24
  const statusIconContainerStyles = css`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `

  const getStatusIconName = (status: NavigationTab['status']): string => {
    switch (status) {
      case 'ready':
        return 'check-circle'
      case 'pending':
        return 'clock'
      case 'incomplete':
        return 'exclamation-circle'
      default:
        return 'clock'
    }
  }

  // Icon color: ready = gold (#f2b400), pending = gray (#797b7e)
  const getStatusIconColor = (status: NavigationTab['status']): string => {
    if (status === 'ready') {
      return colors.PRIMARY_300
    }
    return colors.NEUTRAL_100
  }

  // Icons container - right side
  const iconsContainerStyles = css`
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 0;
  `

  // Icon button - 1px border, 8px padding, 8px border-radius
  const iconButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    background: transparent;
    border: 1px solid ${colors.PRIMARY_300};
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(242, 180, 0, 0.1);
    }
  `

  // Icon wrapper - 16x16
  const iconWrapperStyles = css`
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  `

  return (
    <nav css={containerStyles}>
      <div css={innerContainerStyles}>
        {/* Tabs */}
        <div css={tabsContainerStyles}>
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId
            return (
              <button
                key={tab.id}
                type='button'
                css={getTabStyles(isActive, tab.disabled)}
                onClick={() => !tab.disabled && onTabChange(tab.id)}
                aria-selected={isActive}
                aria-disabled={tab.disabled}
                role='tab'
              >
                <div css={tabContentStyles}>
                  <p css={getTabTitleStyles(isActive)}>{tab.title}</p>
                  {loading ? (
                    <div css={tabSubtitleShimmerStyles} />
                  ) : (
                    <p css={tabSubtitleStyles}>{tab.subtitle}</p>
                  )}
                </div>
                <div css={statusIconContainerStyles}>
                  {!loading && (
                    <Icon
                      name={getStatusIconName(tab.status)}
                      fontSize={20}
                      color={getStatusIconColor(tab.status)}
                    />
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* Icon Buttons */}
        <div css={iconsContainerStyles}>
          {onDownload && (
            <button
              type='button'
              css={iconButtonStyles}
              onClick={onDownload}
              aria-label='Download schedule'
            >
              <div css={iconWrapperStyles}>
                <Icon name='download' fontSize={16} color={colors.WHITE} />
              </div>
            </button>
          )}
          {onSettings && (
            <button
              type='button'
              css={iconButtonStyles}
              onClick={onSettings}
              aria-label='Schedule settings'
            >
              <div css={iconWrapperStyles}>
                <Icon name='cog' fontSize={16} color={colors.WHITE} />
              </div>
            </button>
          )}
          {onMoreOptions && (
            <button
              type='button'
              css={iconButtonStyles}
              onClick={onMoreOptions}
              aria-label='More options'
            >
              <div css={iconWrapperStyles}>
                <Icon name='ellipsis-v' fontSize={16} color={colors.WHITE} />
              </div>
            </button>
          )}
        </div>
      </div>
    </nav>
  )
}

export default SchedulerNavigationBar
